import React, {useEffect, useState} from 'react';
import {CustomCard} from "../../../../../components/UI/AntCustom/CustomCard";
import {Table, Tag, Button, message} from "antd";
import {useIntl, defineMessages} from "react-intl";
import {useAppSelector} from "../../../../../store/store";
import {BillingApi} from "../../../../../API/ApiClasses/BillingApi";
import {BillingApiDownloadInvoiceRequest, EmptyRequest, InvoiceEntity} from "../../../../../proto/generated/api_entities_pb";
import {Loader} from "../../../../../components/UI/Loader/Loader";
import dayjs from "dayjs";
import {DownloadOutlined} from "@ant-design/icons";

const InvoiceHistoryMessages = defineMessages({
  title: {
    id: "invoiceHistory.title",
    defaultMessage: "Invoice History",
  },
  invoiceNumber: {
    id: "invoiceHistory.invoiceNumber",
    defaultMessage: "Invoice #",
  },
  date: {
    id: "invoiceHistory.date",
    defaultMessage: "Date",
  },
  amount: {
    id: "invoiceHistory.amount",
    defaultMessage: "Amount",
  },
  status: {
    id: "invoiceHistory.status",
    defaultMessage: "Status",
  },
  statusPaid: {
    id: "invoiceHistory.statusPaid",
    defaultMessage: "Paid",
  },
  statusPending: {
    id: "invoiceHistory.statusPending",
    defaultMessage: "Pending",
  },
  download: {
    id: "invoiceHistory.download",
    defaultMessage: "Download",
  },
  noInvoices: {
    id: "invoiceHistory.noInvoices",
    defaultMessage: "No invoices found",
  },
  description: {
    id: "invoiceHistory.description",
    defaultMessage: "Description",
  },
  downloadError: {
    id: "invoiceHistory.downloadError",
    defaultMessage: "Failed to download invoice",
  },
});

export const InvoiceHistory: React.FC = () => {
  const intl = useIntl();
  const user = useAppSelector(state => state.user.oneItemObject.item);
  const invoiceListRefreshTrigger = useAppSelector(state => state.tariff.invoiceListRefreshTrigger ?? 0);
  const [invoices, setInvoices] = React.useState<InvoiceEntity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        // Use Stripe invoice list (Option B - fetch directly from Stripe)
        const req = new EmptyRequest();
        const res = await BillingApi.getStripeInvoiceList(req);

        if (res.getResultresponse()?.getIssuccessful()) {
          setInvoices(res.getInvoicelistList().filter((inv) => inv.getAmount() !== 0));
        }
      } catch (error) {
        console.error('Failed to fetch invoices:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchInvoices();
    }
  }, [user, invoiceListRefreshTrigger]);

  const handleDownload = async (record: InvoiceEntity) => {
    const invoiceId = record.getInvoiceid();
    const stripeInvoiceId = record.getStripeinvoiceid();
    const downloadKey = stripeInvoiceId || invoiceId.toString();

    try {
      setDownloadingIds(prev => new Set(prev).add(downloadKey));
      const req = new BillingApiDownloadInvoiceRequest();
      req.setInvoiceid(invoiceId);
      if (stripeInvoiceId) {
        req.setStripeinvoiceid(stripeInvoiceId);
      }
      const res = await BillingApi.downloadInvoice(req);

      if (res.getResultresponse()?.getIssuccessful() && res.getInvoicefile()) {
        const blob = new Blob([res.getInvoicefile()], {type: 'application/pdf'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.getFilename() || `invoice-${stripeInvoiceId || invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        message.error(intl.formatMessage(InvoiceHistoryMessages.downloadError));
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
      message.error(intl.formatMessage(InvoiceHistoryMessages.downloadError));
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(downloadKey);
        return next;
      });
    }
  };

  const getStatusTag = (status: number) => {
    if (status === 2) {
      return <Tag color="green">{intl.formatMessage(InvoiceHistoryMessages.statusPaid)}</Tag>;
    }
    return <Tag color="orange">{intl.formatMessage(InvoiceHistoryMessages.statusPending)}</Tag>;
  };

  const formatAmount = (amount: number, currency?: any) => {
    const currencyCode = currency?.getName() || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const parsed = dayjs(dateStr);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
  };

  const columns = [
    {
      title: intl.formatMessage(InvoiceHistoryMessages.invoiceNumber),
      key: 'invoiceNumber',
      render: (_: any, record: InvoiceEntity) =>
        record.getInvoicenumber() || (record.getStripeinvoiceid() ? record.getStripeinvoiceid() : `#${record.getInvoiceid()}`),
    },
    {
      title: intl.formatMessage(InvoiceHistoryMessages.date),
      key: 'date',
      render: (_: any, record: InvoiceEntity) => formatDate(record.getDateadded()),
    },
    {
      title: intl.formatMessage(InvoiceHistoryMessages.description),
      key: 'description',
      render: (_: any, record: InvoiceEntity) => record.getDescription() || '—',
    },
    {
      title: intl.formatMessage(InvoiceHistoryMessages.amount),
      key: 'amount',
      render: (_: any, record: InvoiceEntity) => formatAmount(record.getAmount(), record.getCurrency()),
    },
    {
      title: intl.formatMessage(InvoiceHistoryMessages.status),
      key: 'status',
      render: (_: any, record: InvoiceEntity) => getStatusTag(record.getStatus()),
    },
    {
      title: '',
      key: 'actions',
      render: (_: any, record: InvoiceEntity) => (
        <Button
          type="link"
          icon={<DownloadOutlined/>}
          loading={downloadingIds.has(record.getStripeinvoiceid() || record.getInvoiceid().toString())}
          onClick={() => handleDownload(record)}
        >
          {intl.formatMessage(InvoiceHistoryMessages.download)}
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <CustomCard width={"max"} style={{ width: '100%' }}>
        <Loader />
      </CustomCard>
    );
  }

  return (
    <CustomCard width={"max"} style={{ width: '100%' }}>
      <div style={{fontSize: '16px', fontWeight: 600, marginBottom: '8px'}}>
        {intl.formatMessage(InvoiceHistoryMessages.title)}
      </div>
      {invoices.length === 0 ? (
        <div style={{textAlign: 'center', padding: '24px', color: '#999999'}}>
          {intl.formatMessage(InvoiceHistoryMessages.noInvoices)}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey={(record) => record.getStripeinvoiceid() || record.getInvoiceid().toString()}
          pagination={{pageSize: 10}}
        />
      )}
    </CustomCard>
  );
};
